import React from 'react'
import * as rn from 'react-native'

import * as c from '../../../common'
import { contractWatermarkSrc } from '../../../common/helpers/contractWatermark'
import { hideHomeCamCashLogo } from '../../../common/helpers/homeCamCashLogo'
import {
  contractLogoWidth,
  reportContractLogo,
} from '../../../common/helpers/reportContractLogo'
import { contractCompaniesForPicker } from '../../../common/helpers/retiredContractCompanies'
import { isSigningFlowV2 } from '../../../common/helpers/signingFlowV2'
import * as r from '../../../react-utils'

import * as m from '@mui/material'
import Entypo from 'react-native-vector-icons/Entypo'
import { useParams } from 'react-router-dom'

import * as gs from '../../../app/gStyles'
import * as cpm from '../../../app/components'
// HOMENOW is green everywhere (Luis Oct 30, same as app icon/login; was gray since Sep 28)
import homenowGrayLogo from '../../img/homenow-green.png'
// Firebase still holds a padded ROOFNOW placeholder; same artwork, trimmed
// (reportContractLogo, pre-deploy QA Nov 1).
import roofnowTrimmedLogo from '../../img/roofnow-trimmed.png'
import { CompanySelectorReadyContext } from './companySelectorLoad'

export type ContractLogoProps = c.r<{
  isPDF?: boolean
  /**
   * V43-style gate: must be true during public token signing (?mode=sign).
   * Unlike ContractLogoOtherFields' plain input `disabled`, this also has to
   * stop the Pressable from opening the picker at all, not just block the
   * resulting Select's onChange.
   */
  readOnly?: boolean
  size: 's' | 'm' | 'l'
}>

export default React.memo<ContractLogoProps>(function ContractLogo({
  isPDF,
  readOnly,
  size,
}) {
  const { customerID } = useParams<'customerID'>()

  const {
    contractCompaniesCurrState,
    contractCompaniesCurrStateArr,
    contractCompanyShortName,
    contractCompanyFull,
    handleContractCompany,
  } = r.useContractCompany(customerID!)
  const { paymentMethodVal } = r.usePayment(customerID!)
  const [customerData] = r.useCustomer(customerID!)
  const noLogo = hideHomeCamCashLogo({
    paymentMethod: paymentMethodVal,
    shortName: contractCompanyShortName,
    signingV2: isSigningFlowV2(customerData),
  })
  const selectedLogo = reportContractLogo(
    contractCompanyShortName,
    contractCompanyFull.logo,
    homenowGrayLogo,
    roofnowTrimmedLogo,
  )

  const companySelected = !!contractCompanyShortName
  const companySelectedIsInCurrList =
    !!contractCompaniesCurrState[contractCompanyShortName]
  const pickerCompanies = React.useMemo(
    () =>
      contractCompaniesForPicker(
        contractCompaniesCurrStateArr,
        contractCompanyShortName,
      ),
    [contractCompaniesCurrStateArr, contractCompanyShortName],
  )
  const moreThanOneCompanyAvail = pickerCompanies.length > 1

  const [choosingCompany, , toggleChoosingCompany] = r.useBool(false)
  // Not before the customer is loaded (companySelectorLoad.ts).
  const selectorReady = React.useContext(CompanySelectorReadyContext)
  const blocked = !!readOnly || !selectorReady

  return (
    <>
      <rn.Pressable
        onPress={blocked ? undefined : toggleChoosingCompany}
        style={gs.rowCentered}
      >
        {companySelected && companySelectedIsInCurrList ? (
          noLogo ? (
            // Cash + HomeCam: no logo on the document; the rep still sees
            // which company is picked so the selector stays usable.
            isPDF || readOnly ? null : (
              <cpm.Text colorScheme="light">
                HomeCam (no logo on cash deals)
              </cpm.Text>
            )
          ) : selectedLogo ? (
            <img
              alt={contractCompanyShortName}
              src={selectedLogo}
              style={{
                width: contractLogoWidth(contractCompanyShortName, size),
              }}
            />
          ) : (
            <cpm.Text colorScheme="light">
              {contractCompanyFull.longName}
            </cpm.Text>
          )
        ) : (
          <cpm.Text colorScheme="light">
            {selectorReady
              ? 'Click here to select a company for the contract'
              : 'Loading customer...'}
          </cpm.Text>
        )}

        {!isPDF &&
          !blocked &&
          (moreThanOneCompanyAvail || !contractCompanyShortName) && (
            <>
              {gs.colGap8}

              <Entypo
                color={c.light.canvas.opaque}
                name="select-arrows"
                size={18}
                style={gs.alignSelfEnd}
              />
            </>
          )}
      </rn.Pressable>

      <m.Select
        onChange={blocked ? undefined : handleContractCompany}
        onClose={toggleChoosingCompany}
        onOpen={toggleChoosingCompany}
        open={!blocked && choosingCompany}
        renderValue={c.alwaysNull}
        style={sx.select}
        value={contractCompanyShortName}
      >
        {pickerCompanies.map(({ logo, longName, shortName }) => (
          <m.MenuItem key={shortName} style={sx.menuItem} value={shortName}>
            {logo && <img alt={longName} src={logo} style={sx.pickerLogo} />}

            <rn.View style={gs.rowCentered}>
              {gs.colGap8}
              <cpm.Text colorScheme="light" size={18}>
                {longName}
              </cpm.Text>
            </rn.View>

            {gs.rowGap8}
          </m.MenuItem>
        ))}
      </m.Select>
    </>
  )
})

/**
 * Watermark for the pages of a contract (PageReport `watermark`): the same
 * logo ContractLogo shows, only while `signed` is false (V67).
 */
export const useContractWatermark = (
  customerID: string,
  signed: boolean,
): string | undefined => {
  const {
    contractCompaniesCurrState,
    contractCompanyFull,
    contractCompanyShortName,
  } = r.useContractCompany(customerID)
  const { paymentMethodVal } = r.usePayment(customerID)
  const [customerData] = r.useCustomer(customerID)

  if (
    hideHomeCamCashLogo({
      paymentMethod: paymentMethodVal,
      shortName: contractCompanyShortName,
      signingV2: isSigningFlowV2(customerData),
    })
  )
    return undefined

  return contractWatermarkSrc({
    homenowGray: homenowGrayLogo,
    listed: !!contractCompaniesCurrState[contractCompanyShortName],
    liveLogo: contractCompanyFull.logo,
    roofnowTrimmed: roofnowTrimmedLogo,
    shortName: contractCompanyShortName,
    signed,
  })
}

export type ContractLogoOtherFieldsProps = c.r<{
  /**
   * V43: must be true during public token signing (?mode=sign) and once the
   * contract is signed — these inputs persist to Firebase via
   * useCustomerFieldInput, so they need the same gating as co-buyer & co.
   */
  disabled?: boolean
  isPDF?: boolean
}>

/**
 * Editable "Other (Cash)"/"Other (Financing)" company fields (name, address,
 * phone, logo). Rendered as a SEPARATE component from `ContractLogo` because
 * the parent contract templates wrap `<ContractLogo>` in an absolutely
 * positioned container sized for just the small logo image — appending these
 * fields there would overlap the contract text below instead of pushing it
 * down. Render this wherever normal document flow is wanted instead.
 */
export const ContractLogoOtherFields = React.memo<ContractLogoOtherFieldsProps>(
  function ContractLogoOtherFields({ disabled, isPDF }) {
    const { customerID } = useParams<'customerID'>()

    const { contractCompanyShortName } = r.useContractCompany(customerID!)

    const isOtherCompany =
      contractCompanyShortName === r.OTHER_CASH_SHORT_NAME ||
      contractCompanyShortName === r.OTHER_FIN_SHORT_NAME

    const [otherName, , otherNameHandle] = r.useCustomerFieldInput(
      customerID!,
      'other_contract_company_name',
    )
    const [otherAddress, , otherAddressHandle] = r.useCustomerFieldInput(
      customerID!,
      'other_contract_company_address',
    )
    const [otherPhone, , otherPhoneHandle] = r.useCustomerFieldInput(
      customerID!,
      'other_contract_company_phone',
    )
    const [otherLicense, , otherLicenseHandle] = r.useCustomerFieldInput(
      customerID!,
      'other_contract_company_license',
    )
    const [, otherLogoWrite] = r.useCustomerFieldInput(
      customerID!,
      'other_contract_company_logo',
    )

    const handleOtherLogoFile = React.useCallback(
      (e: React.ChangeEvent<HTMLInputElement>): void => {
        const file = e.target.files?.[0]
        if (!file) return
        const reader = new FileReader()
        reader.onload = () => {
          otherLogoWrite(reader.result as string)
        }
        reader.readAsDataURL(file)
      },
      [otherLogoWrite],
    )

    if (!isOtherCompany || isPDF) return null

    return (
      <rn.View style={sx.otherFieldsContainer}>
        <cpm.Text colorScheme="light" size={14}>
          Company details for this contract:
        </cpm.Text>

        {gs.rowGap8}

        <input
          disabled={disabled}
          onChange={otherNameHandle}
          placeholder="Company name"
          style={sx.otherInput}
          title="Company name"
          value={otherName}
        />

        <input
          disabled={disabled}
          onChange={otherAddressHandle}
          placeholder="Company address"
          style={sx.otherInput}
          title="Company address"
          value={otherAddress}
        />

        <input
          disabled={disabled}
          onChange={otherPhoneHandle}
          placeholder="Company phone number"
          style={sx.otherInput}
          title="Company phone number"
          value={otherPhone}
        />

        <input
          disabled={disabled}
          onChange={otherLicenseHandle}
          placeholder="Company license #"
          style={sx.otherInput}
          title="Company license #"
          value={otherLicense}
        />

        <input
          accept="image/*"
          disabled={disabled}
          onChange={handleOtherLogoFile}
          title="Company logo"
          type="file"
        />
      </rn.View>
    )
  },
)

const sx = {
  // contain: logos are not all 2.5:1; never stretch them to fit the box.
  // flexShrink 0: a portrait logo (ESR, 640x794) has a content-based minimum
  // width of 40 * 640/794 = 32px, so the row's flexbox shrank its box from
  // 100 to 32 and the name started 68px left of every other row (pre-deploy
  // QA, Nov 1). The box now stays 100x40 for every logo.
  pickerLogo: {
    flexShrink: 0,
    height: 40,
    objectFit: 'contain',
    width: 100,
  } as const,
  menuItem: {
    alignItems: 'stretch',
    flexDirection: 'row',
    paddingBottom: 24,
    paddingTop: 24,
  } as const,
  otherFieldsContainer: {
    // The logo above this is absolutely positioned (top: 96px in
    // FirstElectricContract's sx.logo) and contributes 0 height to normal
    // flow, so this needs its own top offset to clear it visually instead of
    // rendering underneath it.
    marginTop: 160,
  } as const,
  otherInput: {
    display: 'block',
    marginBottom: 6,
    width: 260,
  } as const,
  select: { ...gs.width72, height: 0, opacity: 0 },
}
